import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { useOrgMembers } from "../../../hooks/resources.ts";
import type { OrgMember } from "../../../api/client.ts";
import UserAvatar from "../../../components/UserAvatar.tsx";

interface AssigneePickerProps {
  orgId: string;
  value: string[];
  onChange: (userIds: string[]) => void;
}

function memberLabel(m: OrgMember): string {
  return m.user_display_name?.trim() || m.user_email;
}

/** Multi-select picker over the org's members (people with a Hive account). */
export default function AssigneePicker({ orgId, value, onChange }: AssigneePickerProps) {
  const { data: members = [], isLoading } = useOrgMembers(orgId);

  const selected = value
    .map((id) => members.find((m) => m.user_id === id))
    .filter((m): m is OrgMember => !!m);

  return (
    <Autocomplete
      multiple
      size="small"
      options={members}
      value={selected}
      loading={isLoading}
      disableCloseOnSelect
      getOptionLabel={memberLabel}
      isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
      onChange={(_, next) => onChange(next.map((m) => m.user_id))}
      renderOption={(props, m) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <UserAvatar
                displayName={m.user_display_name}
                email={m.user_email}
                size={24}
              />
              {memberLabel(m)}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label="Assignees" placeholder="Assign people" />
      )}
    />
  );
}
